/**
 * Client-side analytics: posts events to the `analytics.trackEvent` API with
 * session and feature context.
 *
 * A session key groups the events from one page load. It is minted when the
 * bundle loads and replaced on the first event more than an hour later, so it
 * is neither a visit nor a person: navigating within the app keeps the same
 * key, while a reload or a link that opens a new tab starts a fresh one.
 *
 * `trackEvent` enriches every call with that key, the active
 * silo/structure/instance, the enabled-feature map for a structure that asks
 * to record one, admin status, and the device info captured when the session
 * started. The public API is intentionally non-async (returns `void`):
 * analytics is fire-and-forget by design, and encoding that in the signature
 * avoids the need for callers to wrap every call in `fireAndForget`.
 *
 * It is deliberately resilient. It takes a null `Datastore`, so a caller with
 * no instance mounted still records an event, reading the silo off the URL —
 * boot-time perf marks arrive that way. It swallows its own exceptions, since
 * a logging failure must never feed back into more logging, and it goes
 * through `data/analytics`, which holds an API client of its own, for the same
 * reason. Two cases record nothing: a `Datastore` in test mode, so a demo or a
 * story driving real UI stays out of the log, and a page with no silo in its
 * URL at all, which is only ever a redirect or a not-found.
 */

import type { TrackEventInput } from '@repo/shared/orpc/procedures/analytics';
import { trackEventAsync } from 'data/analytics';
import { getDeviceInfo } from 'platform-specific/deviceinfo';
import { getSiloKeyFromWindowUrl } from 'platform-specific/url';
import { useEffect, useState } from 'react';

import type { Datastore } from './datastore';
import { getDeploymentDefaultFeatures } from './features';
import { fireAndForget } from './hooks';

const hourMillis = 60 * 60 * 1000;

let globalSessionKey: string | null = null;
let globalSessionTime: number | null = null;
let globalDeviceInfo: TrackEventInput['deviceInfo'] = null;

/**
 * The feature map to log, which is empty unless the structure asks for one
 * with `logsFeatures`. Only a space does today, so only a conversation's
 * events carry the toggles that shaped it; a structure that wants its own
 * recorded says so on its module rather than being named here.
 */
function getEnabledFeatures(datastore: Datastore | null): Record<string, boolean> {
    if (!datastore) {
        return {};
    }
    const structureModule = datastore.getStructureModule();
    if (!structureModule.logsFeatures) {
        return {};
    }

    const deploymentDefaults = getDeploymentDefaultFeatures(datastore.getSiloKey(), datastore.getStructureKey());
    return { ...structureModule.defaultFeatures, ...deploymentDefaults, ...datastore.getActiveFeatures() };
}

/**
 * Round-trips event params through JSON so they satisfy the contract's
 * JSON-record schema, which refuses anything that is not JSON. Callers pass
 * whatever object is at hand, so this drops `undefined` values and stands a
 * marker in for a circular one rather than letting a param nobody meant to be
 * load-bearing cost the whole event.
 */
function toLoggableParams(params: Record<string, any> | undefined): TrackEventInput['params'] {
    try {
        return JSON.parse(JSON.stringify(params ?? {}));
    } catch (e) {
        return { error: 'Non-serializable object' };
    }
}

export function trackEvent(datastore: Datastore | null, eventType: string, params?: Record<string, any>): void {
    fireAndForget(trackEventInternalAsync(datastore, eventType, params));
}

async function trackEventInternalAsync(datastore: Datastore | null, eventType: string, params?: Record<string, any>) {
    try {
        if (datastore?.getIsTestMode()) {
            return;
        }

        const siloKey = datastore?.getSiloKey() || getSiloKeyFromWindowUrl();
        if (!siloKey) {
            return;
        }

        const roles = datastore?.sessionData?.roles;

        if (!globalSessionKey || !globalSessionTime || Date.now() - globalSessionTime > hourMillis) {
            globalSessionKey = crypto.randomUUID();
            globalSessionTime = Date.now();
            globalDeviceInfo = getDeviceInfo();
        }

        await trackEventAsync({
            eventType,
            params: toLoggableParams(params),
            sessionKey: globalSessionKey,
            deviceInfo: globalDeviceInfo,
            siloKey,
            // `||` rather than `??`: the contract's key schema rejects the
            // empty string, and an unmounted instance reads as one.
            structureKey: datastore?.getStructureKey() || null,
            instanceKey: datastore?.getInstanceKey() || null,
            isAdmin: roles ? roles.length > 0 : null,
            enabledFeatures: getEnabledFeatures(datastore),
        });
    } catch (e) {
        // Exceptions during logging need to be swallowed, otherwise we'll get
        // stuck in an infinite loop
        console.error('Error logging event', e);
    }
}

export function useStartTime() {
    const [startTime, setStartTime] = useState(Date.now());
    useEffect(() => {
        setStartTime(Date.now());
    }, []);
    return startTime;
}
